/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { CartLineItemComponent, OrderDetailsProduct } from '@msdyn365-commerce/components';
import { IGridSettings, IImageSettings, Image, IRequestContext } from '@msdyn365-commerce/core';
import { ChannelDeliveryOptionConfiguration, format, ProductDeliveryOptions, SalesLine, Shipment, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, getFallbackImageUrl, getProductPageUrlSync, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { Observer } from 'mobx-react';
import * as React from 'react';

import Address from './address';
import { BuySelectedComponent } from './buy-selected-component';
import LabelComponent from './label';
import { SalesLineState } from './sales-line-state';
import { defaultImageSettings, getDateTimeslot, getSalesStatusCode, IDeliveryType, IModuleComponentProps, ISalesStatusCode } from './utilities';

export type ISalesStatusStringMap = {
    [x in ISalesStatusCode]: string | undefined;
};

export type IDeliveryTypeStringMap = {
    [x in IDeliveryType]: string | undefined;
};

export interface ISalesLineResources {
    productDimensionTypeSize: string;
    productDimensionTypeColor: string;
    productDimensionTypeStyle: string;
    productDimensionTypeAmount?: string;
    salesLineQuantityText: string;
    configString: string;
    shipToLabel: string;
    storeLabel: string;
    phoneLabel: string;
    phoneAriaLabel: string;
    buyItAgainAriaLabel: string;
    buyItAgainLabel: string;
    orderStatusReadyForPickup: string;
    orderStatusPickedUp: string;
    orderStatusShipped: string;
    orderStatusCanceled: string;
    orderStatusProcessing: string;
    pickedUpSalesLines: string;
    deliveredSalesLines: string;
    carryOutSalesLines: string;
    trackingLabel: string;
    trackingAriaLabel: string;
    trackingComingSoonLabel: string;
    discountStringText: string;
    orderStatusEmailed: string;
    emailSalesLines: string;
    pickupDateTimeslotText: string;
    pickupTimeslotPlaceHolder: string;
    shippingCharges?: string;
    orderDetailsUnavailableProductText?: string;
    orderDetailsBuySelectedButtonText?: string;
    orderDetailsBuySelectedButtonAriaLabel?: string;
    orderDetailsBuySelectedAddingToCartErrorNotificationTitle?: string;
    orderDetailsBuySelectedAddingToCartErrorNotificationCloseAriaLabel?: string;
    orderDetailsGoToCartText?: string;
    orderDetailsDialogCloseText?: string;
    orderDetailsSelectRadioAriaLabelText?: string;
    orderDetailsSingleItemText?: string;
    orderDetailsMultiLineItemFormatText?: string;
    orderDetailsMultiLinesFormatText?: string;
    orderDetailsHeaderMessageText?: string;
    orderDetailsMultiItemsValidationErrorMessage?: string;
    orderDetailsOneErrorText?: string;
    orderDetailsMultiErrorText?: string;
    addedQuantityText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    freePriceText?: string;
}

export interface IGetSalesLineInput {
    moduleName: string;
    moduleComponentProps: IModuleComponentProps;
    product?: SimpleProduct;
    shipment?: Shipment;
    salesLine: SalesLine;
    isCurrentChannel: boolean;
    resources: ISalesLineResources;
    imageOnly?: boolean;
    salesLineState?: SalesLineState;
    showCompleteAddress?: boolean;
    showTimeslot: boolean;
    telemetryContent?: ITelemetryContent;
    retailMultiplePickUpOptionEnabled?: boolean;
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;
    productDeliveryOptions?: ProductDeliveryOptions[];
    isSelectionModeEnabled?: boolean;
    orderDetailsProduct?: OrderDetailsProduct;
    isReorderingEnabled?: boolean;
}

export interface ISalesLine {
    salesLineProps: INodeProps;
    salesLine: React.ReactNode;
    address?: React.ReactNode;
    buyAgainButton?: React.ReactNode;
    salesStatus: React.ReactNode;
    deliveryType: React.ReactNode;
    trackingInfo: React.ReactNode;
    pickupDateTimeslot: React.ReactNode;
    errors?: React.ReactNode;

    // Row data
    data: {
        salesLine: SalesLine;
        product?: SimpleProduct;
        shipment?: Shipment;
        deliveryType: string;
        productUrl: string;
        salesStatus: ISalesStatusCode;
        isChecked?: boolean;
    };
}

enum deliveryTypeEnum {
    ship = 'ship',
    email = 'email',
    carryOut = 'carryOut',
    pickUp = 'pickUp'
}

/**
 * The on select line call back.
 * @param orderDetailsProduct -- The current order details product.
 * @returns The call back method.
 */
const onSelectLine = (orderDetailsProduct: OrderDetailsProduct) => () => {
    orderDetailsProduct.isSelected = !orderDetailsProduct.isSelected;
};

/**
 * Get the checkbox element.
 * @param orderDetailsProduct -- The order details product.
 * @param orderDetailsSelectRadioAriaLabelText -- The aria label text.
 * @param moduleName -- The module name.
 * @returns The checkbox element.
 */
const getOrderDetailsSalesLineCheckbox = (orderDetailsProduct: OrderDetailsProduct,
    orderDetailsSelectRadioAriaLabelText: string | undefined,
    moduleName: string): JSX.Element | null => {

    const checkboxRowAriaLabel = format(orderDetailsSelectRadioAriaLabelText ?? '{0}', orderDetailsProduct.salesLine.ProductId);

    return (
        <div className={`${moduleName}__sales-line-checkbox`}>
            <label className={classnames(`${moduleName}__checkbox-container`, 'checkbox-container')}>
                <input
                    className={classnames(`${moduleName}__checkbox-input`, 'checkbox-input')}
                    type='checkbox'
                    checked={orderDetailsProduct.isSelected}
                    aria-label={checkboxRowAriaLabel}
                    aria-checked={orderDetailsProduct.isSelected}
                    onChange={onSelectLine(orderDetailsProduct)}
                />
                <span className={classnames(`${moduleName}__checkmark`, 'checkmark')} />
            </label>
        </div>
    );
};

/**
 * Get tracking information.
 * @param deliveryType -- The delivery type.
 * @param moduleName -- Module name.
 * @param trackingLabel -- Tracking label.
 * @param trackingAriaLabel -- Tracking aria label.
 * @param trackingComingSoonLabel -- Tracking coming soon label.
 * @param shipment -- Shipment.
 * @returns The tracking information.
 */
export const getTrackingInfo = (deliveryType: string,
    moduleName: string,
    trackingLabel: string,
    trackingAriaLabel: string,
    trackingComingSoonLabel: string,
    shipment: Shipment | undefined): false | JSX.Element => {
    return deliveryType === deliveryTypeEnum.ship && (
        <span className={`${moduleName}__tracking-info`}>
            {trackingLabel}
            {shipment ? (
                <a className={`${moduleName}__tracking-link`} href={shipment.TrackingUrl} aria-label={trackingAriaLabel}>
                    {shipment.TrackingNumber}
                </a>
            ) : (
                <>
                    {trackingComingSoonLabel}
                </>
            )}
        </span>
    );
};

/**
 * Get pick update date time lot information.
 * @param shouldShowTimeslot -- Should show time slot.
 * @param isDeliveryPickupSame -- Is delivery and pickup the same.
 * @param salesLine -- The sales line.
 * @param pickupTimeslotPlaceHolder -- The pickup time slot place holder.
 * @param moduleName -- The module name.
 * @param locale -- The locale.
 * @param pickupDateTimeslotText -- The pickup date time slot text.
 * @returns The pickup date time slot information.
 */
export const getPickupDateTimeslotInfo = (shouldShowTimeslot: boolean,
    isDeliveryPickupSame: boolean,
    salesLine: SalesLine,
    pickupTimeslotPlaceHolder: string,
    moduleName: string,
    locale: string,
    pickupDateTimeslotText: string): false | JSX.Element => {
    const canShowTimeslot: boolean = shouldShowTimeslot && isDeliveryPickupSame &&
    salesLine.PickupTimeslotStartDateTime !== undefined && salesLine.PickupTimeslotEndDateTime !== undefined;

    const pickupDateTimeslot = canShowTimeslot ? getDateTimeslot(
        pickupTimeslotPlaceHolder, salesLine.PickupTimeslotStartDateTime, salesLine.PickupTimeslotEndDateTime, locale) : '';

    return canShowTimeslot && (
        <div className={`${moduleName}__pickup-date-timeslot`}>
            <div className={`${moduleName}__pickup-date-timeslot-title`}>
                {pickupDateTimeslotText}
            </div>
            <div className={`${moduleName}__pickup-date-timeslot-content`}>
                {pickupDateTimeslot}
            </div>
        </div>);
};

/**
 * Get sales status.
 * @param orderStatusCanceled -- Order status canceled.
 * @param orderStatusReadyForPickup -- Order status ready for pick up.
 * @param orderStatusPickedUp -- Order status pick up.
 * @param orderStatusProcessing -- Order status processing.
 * @param orderStatusShipped -- Order status shipped.
 * @param orderStatusEmailed -- Order status emailed.
 * @returns The sales status in string.
 */
export const getSalesStatusStringMap = (orderStatusCanceled: string | undefined,
    orderStatusReadyForPickup: string | undefined,
    orderStatusPickedUp: string | undefined,
    orderStatusProcessing: string | undefined,
    orderStatusShipped: string | undefined,
    orderStatusEmailed: string | undefined): ISalesStatusStringMap => {
    return {
        canceled: orderStatusCanceled,
        readyForPickup: orderStatusReadyForPickup,
        pickedUp: orderStatusPickedUp,
        processing: orderStatusProcessing,
        shipped: orderStatusShipped,
        unknown: undefined,
        emailed: orderStatusEmailed
    };
};

/**
 * Get delivery type.
 * @param deliveryTypeString -- Delivery type string.
 * @param moduleName -- Module name.
 * @param deliveryTypeClass -- Delivery type class.
 * @param storePickUp -- Store pick up.
 * @param isMultiplePickUpOptionEnabled -- Is multiple pick up enabled.
 * @returns The delivery type react node.
 */
export const getDeliveryTypeComp = (deliveryTypeString: string | undefined,
    moduleName: string,
    deliveryTypeClass: string | undefined,
    storePickUp: string | null | undefined,
    isMultiplePickUpOptionEnabled: boolean | undefined): React.ReactNode => {

    const deliveryTypePickup = deliveryTypeString &&
    <LabelComponent className={`${moduleName}__devliery-type ${deliveryTypeClass ?? ''}-icon`} label={`${deliveryTypeString} ${storePickUp ?? ''}`} />;
    const deliveryTypeNoPickup = deliveryTypeString && <LabelComponent className={`${moduleName}__devliery-type`} label={deliveryTypeString} />;

    return isMultiplePickUpOptionEnabled ? deliveryTypePickup : deliveryTypeNoPickup;
};

/**
 * Renders sales line picture.
 * @param moduleName - Module name.
 * @param requestContext - Request context.
 * @param productName - Product name.
 * @param product - Product data.
 * @param fallbackImageUrl - Fallback image.
 * @param gridSettings - Grid settings.
 * @param imageSettings - Image settings.
 * @param salesLineState - Product state.
 * @returns React node.
 */
const getSalesLinePicture = (
    moduleName: string, requestContext: IRequestContext,
    productName: string | undefined, product: SimpleProduct | undefined,
    fallbackImageUrl: string | undefined, gridSettings: IGridSettings, imageSettings: IImageSettings | undefined, salesLineState?: SalesLineState) => {
    return (
        <Observer>
            {() => (
                !salesLineState?.isHidden && <div className={`${moduleName}__sales-line-picture`}>
                    <Image
                        requestContext={requestContext}
                        className={`${moduleName}__sales-line-image`}
                        altText={productName}
                        src={product?.PrimaryImageUrl ?? ''}
                        fallBackSrc={fallbackImageUrl}
                        gridSettings={gridSettings}
                        imageSettings={imageSettings ?? defaultImageSettings}
                        loadFailureBehavior='empty'
                    />
                </div>
            )}
        </Observer>
    );
};

/**
 * Renders sales line.
 * @param props - Sales line props.
 * @returns React node.
 */
const SalesLineComponent: React.FC<IGetSalesLineInput> = (props: IGetSalesLineInput) => {
    const productName = props.product?.Name ?? props.salesLine.Description;
    const requestContext = props.moduleComponentProps.context.actionContext.requestContext;

    if (props.imageOnly) {
        const fallbackImageUrl = getFallbackImageUrl(props.product?.ItemId, requestContext.apiSettings);
        return getSalesLinePicture(props.moduleName, requestContext,
            productName, props.product, fallbackImageUrl, requestContext.gridSettings!, props.moduleComponentProps.config.imageSettings,
            props.salesLineState);
    }

    const orderDetailsProducts = props.orderDetailsProduct ?? new OrderDetailsProduct(props.salesLine, props.product);

    const isSelectionModeEnabled = props.isSelectionModeEnabled && orderDetailsProducts.isProductAvailable;

    const orderDetailCheckbox = isSelectionModeEnabled && getOrderDetailsSalesLineCheckbox(
        orderDetailsProducts, props.resources.orderDetailsSelectRadioAriaLabelText, props.moduleName);

    let productUrl = '';
    if (!ObjectExtensions.isNullOrUndefined(productName) && !ObjectExtensions.isNullOrUndefined(props.salesLine.ProductId)) {
        productUrl = getProductPageUrlSync(productName, props.salesLine.ProductId, props.moduleComponentProps.context.actionContext);
    }

    const className = `${props.moduleName}__sales-line-checkbox-control`;

    return (
        <div
            className={
                classnames(
                    className,
                    isSelectionModeEnabled ? `${className}__selection-enabled` : `${className}__selection-disabled`,
                    props.isSelectionModeEnabled ? `${className}__selection-global-enabled` : `${className}__selection-global-disabled`,
                    orderDetailsProducts.isProductAvailable ? `${className}__available-product` : `${className}__unavailable-product`,
                    orderDetailsProducts.isSelected ? `${className}__selected` : `${className}__unselected`
                )
            }
        >
            {orderDetailCheckbox}
            <CartLineItemComponent
                id={props.moduleComponentProps.id}
                typeName={props.moduleComponentProps.typeName}
                context={props.moduleComponentProps.context}
                resources={{
                    sizeString: props.resources.productDimensionTypeSize,
                    colorString: props.resources.productDimensionTypeColor,
                    styleString: props.resources.productDimensionTypeStyle,
                    amountString: props.resources.productDimensionTypeAmount,
                    quantityDisplayString: props.resources.salesLineQuantityText,
                    configString: props.resources.configString,
                    discountStringText: props.resources.discountStringText,
                    inputQuantityAriaLabel: '',
                    originalPriceText: '',
                    currentPriceText: '',
                    shippingChargesText: props.resources.shippingCharges ?? ''
                }}
                primaryImageUrl={props.product?.PrimaryImageUrl}
                productUrl={productUrl}
                imageSettings={props.moduleComponentProps.config.imageSettings ?? defaultImageSettings}
                gridSettings={requestContext.gridSettings!}
                isSalesLine
                data={{ cartLine: props.salesLine, product: props.product }}
                showShippingChargesForLineItems={props.moduleComponentProps.config.showShippingChargesForLineItems}
                channelDeliveryOptionConfig={props.retailMultiplePickUpOptionEnabled ? props.channelDeliveryOptionConfig : undefined}
            />
        </div>
    );
};

export const getSalesLine = (props: IGetSalesLineInput): ISalesLine => {
    const {
        moduleName,
        moduleComponentProps: {
            typeName,
            context,
            config: { imageSettings }
        },
        product,
        shipment,
        salesLine,
        resources: {
            shipToLabel,
            storeLabel,
            phoneLabel,
            phoneAriaLabel,
            buyItAgainLabel,
            buyItAgainAriaLabel,
            orderStatusReadyForPickup,
            orderStatusProcessing,
            orderStatusShipped,
            orderStatusPickedUp,
            orderStatusCanceled,
            orderStatusEmailed,
            pickedUpSalesLines,
            deliveredSalesLines,
            carryOutSalesLines,
            emailSalesLines,
            trackingLabel,
            trackingAriaLabel,
            trackingComingSoonLabel,
            pickupDateTimeslotText,
            pickupTimeslotPlaceHolder,
            orderDetailsUnavailableProductText,
            orderDetailsBuySelectedAddingToCartErrorNotificationTitle,
            orderDetailsBuySelectedAddingToCartErrorNotificationCloseAriaLabel,
            orderDetailsGoToCartText,
            orderDetailsDialogCloseText,
            orderDetailsSingleItemText,
            orderDetailsMultiLineItemFormatText,
            orderDetailsMultiLinesFormatText,
            orderDetailsHeaderMessageText,
            orderDetailsMultiItemsValidationErrorMessage,
            orderDetailsOneErrorText,
            orderDetailsMultiErrorText,
            addedQuantityText,
            originalPriceText,
            currentPriceText,
            freePriceText
        },
        channelDeliveryOptionConfig,
        productDeliveryOptions,
        isSelectionModeEnabled,
        orderDetailsProduct,
        isReorderingEnabled
    } = props;

    const salesStatusStringMap = getSalesStatusStringMap(orderStatusCanceled, orderStatusReadyForPickup, orderStatusPickedUp,
        orderStatusProcessing, orderStatusShipped, orderStatusEmailed);

    const deliveryTypeStringMap: IDeliveryTypeStringMap = {
        pickUp: pickedUpSalesLines,
        ship: deliveredSalesLines,
        carryOut: carryOutSalesLines,
        email: emailSalesLines
    };

    const pickupDeliveryModeCode = context.request.channel?.PickupDeliveryModeCode;
    const emailDeliveryModeCode = context.request.channel?.EmailDeliveryModeCode;
    const productName = product?.Name ?? salesLine.Description;
    let productUrl = '';
    if (!ObjectExtensions.isNullOrUndefined(productName) && !ObjectExtensions.isNullOrUndefined(salesLine.ProductId)) {
        productUrl = getProductPageUrlSync(productName, salesLine.ProductId, context.actionContext);
    }
    const pickupMode = getPickupModeDesc(product, salesLine.DeliveryMode, productDeliveryOptions, props.retailMultiplePickUpOptionEnabled);
    const deliveryType = getDeliveryType(
        pickupMode, salesLine, salesLine.DeliveryMode, pickupDeliveryModeCode,
        props.retailMultiplePickUpOptionEnabled, channelDeliveryOptionConfig, emailDeliveryModeCode);
    const salesStatus = getSalesStatusCode(deliveryType, salesLine.SalesStatusValue, pickupMode);
    const salesStatusString = salesStatusStringMap[salesStatus];

    const salesStatusComp = salesStatusString && <LabelComponent className={`${moduleName}__sales-status`} label={salesStatusString} />;
    const deliveryTypeString = getDeliveryTypeString(deliveryType, deliveryTypeStringMap, props.retailMultiplePickUpOptionEnabled);
    const deliveryTypeClass = getDeliveryTypeClassName(deliveryType, deliveryTypeStringMap);
    const storePickUp = getStoreName(props.retailMultiplePickUpOptionEnabled, salesLine, deliveryType);
    const deliveryTypeComp = getDeliveryTypeComp(deliveryTypeString, moduleName, deliveryTypeClass, storePickUp, props.retailMultiplePickUpOptionEnabled);

    const trackingInfo = getTrackingInfo(deliveryType, moduleName, trackingLabel, trackingAriaLabel, trackingComingSoonLabel, shipment);
    const orderDetailProducts = orderDetailsProduct ?? new OrderDetailsProduct(salesLine, product, productUrl);
    const orderDetailsBuyItAgainAriaLabel = productName && buyItAgainAriaLabel ? (buyItAgainAriaLabel || '').replace('{productName}', productName) : '';
    const pickupDateTimeslotInfo = getPickupDateTimeslotInfo(props.showTimeslot, deliveryType === pickupMode, salesLine,
        pickupTimeslotPlaceHolder, moduleName, context.request.locale, pickupDateTimeslotText);
    let buyItAgainLinkClassName = format('{0}__sales-line-btn-buy-it-again-link', moduleName);
    if (isSelectionModeEnabled) {
        buyItAgainLinkClassName = format('{0} {1}-padding', buyItAgainLinkClassName, buyItAgainLinkClassName);
    }

    if (imageSettings) {
        imageSettings.cropFocalRegion = true;
    }

    const salesLineClassName = `${moduleName}__sales-line`;
    const salesLineFullClassName = classnames(
        salesLineClassName,
        isSelectionModeEnabled ? `${salesLineClassName}__selection-enabled` : `${salesLineClassName}__selection-disabled`,
        props.isSelectionModeEnabled ? `${salesLineClassName}__selection-global-enabled` : `${salesLineClassName}__selection-global-disabled`,
        orderDetailProducts.isProductAvailable ? `${salesLineClassName}__available-product` : `${salesLineClassName}__unavailable-product`);

    return {
        data: {
            salesLine,
            productUrl,
            deliveryType,
            salesStatus,
            product,
            shipment,
            isChecked: orderDetailProducts.isSelected
        },
        salesLineProps: { className: salesLineFullClassName },
        salesLine: SalesLineComponent(props),
        address: salesLine.ShippingAddress && (
            <Address
                moduleName={moduleName}
                address={salesLine.ShippingAddress}
                resource={{ shipToLabel, storeLabel, phoneLabel, phoneAriaLabel }}
                deliveryType={deliveryType}
                showCompleteAddress={props.showCompleteAddress}
            />
        ),
        deliveryType: deliveryTypeComp,
        salesStatus: salesStatusComp,
        trackingInfo,
        buyAgainButton: (
            <BuySelectedComponent
                context={context}
                className={props.retailMultiplePickUpOptionEnabled ? buyItAgainLinkClassName : `${moduleName}__sales-line-btn-buy-it-again`}
                data={{ products: [orderDetailProducts] }}
                resources={{
                    orderDetailsBuyItAgainLabel: buyItAgainLabel,
                    orderDetailsUnavailableProductText,
                    orderDetailsBuyItAgainAriaLabel,
                    orderDetailsBuySelectedAddingToCartErrorNotificationTitle,
                    orderDetailsBuySelectedAddingToCartErrorNotificationCloseAriaLabel,
                    orderDetailsGoToCartText,
                    orderDetailsDialogCloseText,
                    orderDetailsSingleItemText,
                    orderDetailsMultiLineItemFormatText,
                    orderDetailsMultiLinesFormatText,
                    orderDetailsHeaderMessageText,
                    orderDetailsMultiItemsValidationErrorMessage,
                    orderDetailsOneErrorText,
                    orderDetailsMultiErrorText,
                    addedQuantityText,
                    originalPriceText,
                    currentPriceText,
                    freePriceText
                }}
                id={props.moduleComponentProps.id}
                typeName={typeName}
                isReorderingEnabled={isReorderingEnabled}
            />
        ),
        pickupDateTimeslot: pickupDateTimeslotInfo,
        errors: !orderDetailProducts.isProductAvailable && <div className='msc-alert-danger'>
            {props.resources.orderDetailsUnavailableProductText}
        </div>
    };
};

export const getDeliveryType = (pickupMode: string, salesLine: SalesLine, salesLineDeliveryMode?: string, pickupDeliveryModeCode?: string, retailMultiplePickupOption?: boolean,
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration, emailDeliveryModeCode?: string): string => {
    let isPickupDeliveryMode: boolean;

    if (retailMultiplePickupOption) {
        isPickupDeliveryMode = salesLineDeliveryMode === channelDeliveryOptionConfig?.PickupDeliveryModeCodes?.find(dM => dM === salesLineDeliveryMode);
    } else {
        isPickupDeliveryMode = !!pickupDeliveryModeCode && pickupDeliveryModeCode !== '' && pickupDeliveryModeCode === salesLineDeliveryMode;
    }

    return isPickupDeliveryMode ? pickupMode : (emailDeliveryModeCode && emailDeliveryModeCode !== '' && emailDeliveryModeCode === salesLine.DeliveryMode ? deliveryTypeEnum.email : salesLine.DeliveryMode ? deliveryTypeEnum.ship : deliveryTypeEnum.carryOut);
};

export const getDeliveryTypeString = (deliveryType: string, deliveryTypeStringMap: IDeliveryTypeStringMap, retailMultiplePickupOption?: boolean): string | undefined => {
    if (retailMultiplePickupOption) {
        switch (deliveryType) {
            case deliveryTypeEnum.ship:
            case deliveryTypeEnum.carryOut:
            case deliveryTypeEnum.email:
                return deliveryTypeStringMap[deliveryType];
            default:
                return deliveryType;
        }
    } else {
        return deliveryTypeStringMap[deliveryType];
    }
};

export const getDeliveryTypeClassName = (deliveryType: string, deliveryTypeStringMap: IDeliveryTypeStringMap, retailMultiplePickupOption?: boolean): string | undefined => {
    switch (deliveryType) {
        case deliveryTypeEnum.ship:
        case deliveryTypeEnum.carryOut:
        case deliveryTypeEnum.email:
            return deliveryType;
        default:
            return deliveryTypeEnum.pickUp;
    }
};

const defaultDeliveryOption: string = 'pickup';

/**
 * Retrieves the delivery description by product id.
 * @param productRecordId - Product record id.
 * @param salesLineDeliveryMode - Delivery mode from the sales line.
 * @param productDeliveryOptions - Possible delivery options.
 * @returns Delivery option description.
 */
export const getDeliveryDescriptionByProductId = (
    productRecordId?: number, salesLineDeliveryMode?: string,
    productDeliveryOptions?: ProductDeliveryOptions[]): string => {

    if (ObjectExtensions.isNullOrUndefined(productRecordId) || !ArrayExtensions.hasElements(productDeliveryOptions)) {
        return defaultDeliveryOption;
    }

    const productDeliveryOption = productDeliveryOptions.find((currentDeliveryOption) => {
        return currentDeliveryOption.ProductId === productRecordId;
    });

    const deliveryDescription = productDeliveryOption?.DeliveryOptions?.find(option => option.Code === salesLineDeliveryMode)?.Description;

    return deliveryDescription ?? defaultDeliveryOption;
};

export const getPickupModeDesc = (
    product?: SimpleProduct, salesLineDeliveryMode?: string,
    productDeliveryOptions?: ProductDeliveryOptions[], retailMultiplePickupOption?: boolean): string => {

    return getDeliveryDescriptionByProductId(product?.RecordId, salesLineDeliveryMode, productDeliveryOptions);
};

/**
 * Get the store name.
 * @param isMultiplePickUpOptionEnabled -- Is mutiple pick up option enabled.
 * @param salesLine -- Sales line.
 * @param deliveryType -- Delivery type.
 * @returns The store name.
 */
export const getStoreName = (isMultiplePickUpOptionEnabled: boolean | undefined, salesLine: SalesLine, deliveryType?: string): string | undefined | null => {
    if (!isMultiplePickUpOptionEnabled) {
        return null;
    }

    switch (deliveryType) {
        case deliveryTypeEnum.ship:
        case deliveryTypeEnum.email:
        case deliveryTypeEnum.carryOut:
            return '';
        default:
            return `, ${salesLine.ShippingAddress?.Name}`;
    }
};
